import React from 'react';
import { Avatar } from '@mui/material';
import { DoneAll, Done } from '@mui/icons-material';
import styled from 'styled-components';
import { Message } from '../../../Models/Message';

// ============================================================================
// Styled Components - Style moderne Slack/Discord + glassmorphism
// ============================================================================

const MessageRow = styled.div<{ isOwn: boolean }>`
    display: flex;
    gap: 0.75rem;
    max-width: 85%;
    align-self: ${props => props.isOwn ? 'flex-end' : 'flex-start'};
    flex-direction: ${props => props.isOwn ? 'row-reverse' : 'row'};
    animation: messageSlideIn 0.3s ease-out;

    @keyframes messageSlideIn {
        from {
            opacity: 0;
            transform: translateY(8px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    @media (max-width: 768px) {
        max-width: 90%;
        gap: 0.5rem;
    }
`;

const MessageAvatar = styled(Avatar)<{ isOwn: boolean }>`
    width: 36px !important;
    height: 36px !important;
    font-size: 0.8rem !important;
    font-weight: 600 !important;
    flex-shrink: 0;
    background: ${props => props.isOwn
        ? 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))'
        : 'linear-gradient(135deg, #64748b, #475569)'} !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    @media (max-width: 768px) {
        width: 32px !important;
        height: 32px !important;
        font-size: 0.7rem !important;
    }
`;

const MessageContent = styled.div<{ isOwn: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: ${props => props.isOwn ? 'flex-end' : 'flex-start'};
`;

const SenderName = styled.span<{ isOwn: boolean }>`
    font-size: 0.75rem;
    font-weight: 600;
    color: ${props => props.isOwn ? 'var(--primary-color)' : 'var(--text-secondary)'};
    margin-bottom: 0.25rem;
    padding: 0 0.5rem;
`;

const BubbleWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const Bubble = styled.div<{ isOwn: boolean }>`
    padding: 0.875rem 1rem;
    border-radius: 18px;
    font-size: 0.9rem;
    line-height: 1.5;
    word-wrap: break-word;
    word-break: break-word;
    position: relative;
    transition: transform 0.15s ease;

    ${props => props.isOwn ? `
        background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
        color: white;
        border-bottom-right-radius: 6px;
        box-shadow: 0 2px 12px rgba(102, 126, 234, 0.25);

        &:hover {
            transform: scale(1.01);
        }
    ` : `
        background: white;
        color: var(--text-primary);
        border: 1px solid rgba(0, 0, 0, 0.06);
        border-bottom-left-radius: 6px;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);

        &:hover {
            background: rgba(255, 255, 255, 0.95);
            transform: scale(1.01);
        }
    `}

    @media (max-width: 768px) {
        padding: 0.75rem 0.875rem;
        border-radius: 16px;
        font-size: 0.95rem;

        &:hover {
            transform: none;
        }
    }
`;

const MessageMeta = styled.div<{ isOwn: boolean }>`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-top: 0.375rem;
    padding: 0 0.5rem;
    justify-content: ${props => props.isOwn ? 'flex-end' : 'flex-start'};
`;

const TimeStamp = styled.span`
    font-size: 0.7rem;
    color: var(--text-secondary);
    font-weight: 500;
`;

const ReadStatus = styled.span<{ isRead: boolean }>`
    display: flex;
    align-items: center;
    color: ${props => props.isRead ? '#10b981' : 'var(--text-secondary)'};
`;

// ============================================================================
// Component
// ============================================================================

interface MessageBubbleProps {
    message: Message;
    isOwn: boolean;
    showAvatar?: boolean;
    showName?: boolean;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
    message,
    isOwn,
    showAvatar = true,
    showName = true
}) => {
    return (
        <MessageRow isOwn={isOwn}>
            {showAvatar && (
                <MessageAvatar isOwn={isOwn}>
                    {message.getSenderInitials()}
                </MessageAvatar>
            )}

            <MessageContent isOwn={isOwn}>
                {showName && !isOwn && (
                    <SenderName isOwn={isOwn}>
                        {message.senderName}
                    </SenderName>
                )}

                <BubbleWrapper>
                    <Bubble isOwn={isOwn}>
                        {message.content}
                    </Bubble>

                    <MessageMeta isOwn={isOwn}>
                        <TimeStamp>{message.getRelativeTime()}</TimeStamp>
                        {isOwn && (
                            <ReadStatus isRead={message.isRead}>
                                {message.isRead ? (
                                    <DoneAll sx={{ fontSize: 14 }} />
                                ) : (
                                    <Done sx={{ fontSize: 14 }} />
                                )}
                            </ReadStatus>
                        )}
                    </MessageMeta>
                </BubbleWrapper>
            </MessageContent>
        </MessageRow>
    );
};

export default MessageBubble;
